import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ActionSurface } from '@/components/patterns/action-surface'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { ToggleButton } from '@/components/ui/toggle-button'
import { subscriptionFrequencyOptions } from '@/lib/subscription-frequency'
import { formatCurrency } from '@/lib/utils'
import { ShoppingCart } from 'lucide-react'
import { useState } from 'react'
import {
  getPosVariantUnitPrice,
  isPosVariantOutOfStock,
  type PosQuantityByVariantId,
} from '@/lib/pos/inventory'
import type {
  Product,
  SubscriptionFrequency,
  Variant,
} from '@/lib/services/products/products.types'

const SUBSCRIPTION_PACKAGE_CATALOG_TYPE = 'subscription_package'

function isSubscriptionPackageProduct(product: Product): boolean {
  return product?.product_catalog_type === SUBSCRIPTION_PACKAGE_CATALOG_TYPE
}

function getEnabledSubscriptionFrequencyOptions(product: Product) {
  const enabledFrequencies = Array.isArray(product?.subscription_frequency_options)
    ? product.subscription_frequency_options
    : []

  return subscriptionFrequencyOptions.filter((option) => enabledFrequencies.includes(option.id))
}

function getDefaultSubscriptionFrequency(product: Product): SubscriptionFrequency | '' {
  const enabledOptions = getEnabledSubscriptionFrequencyOptions(product)

  return enabledOptions.find((option) => option.id === 'monthly')?.id ?? enabledOptions[0]?.id ?? ''
}

function getSubscriptionFrequencyLabel(frequency: SubscriptionFrequency | ''): string {
  return subscriptionFrequencyOptions.find((option) => option.id === frequency)?.name ?? frequency
}

export function VariantSelectionModal({
  open,
  onOpenChange,
  product,
  quantityByVariantId = {},
  onSelectVariant,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  product: Product | null
  quantityByVariantId?: PosQuantityByVariantId
  /** A subscription package's option comes with the chosen frequency. */
  onSelectVariant: (
    variant: Variant,
    options: { subscriptionFrequency?: SubscriptionFrequency | '' }
  ) => void
}) {
  const [subscriptionFrequencyState, setSubscriptionFrequencyState] = useState<{
    productId: number | null
    value: SubscriptionFrequency | ''
  }>({
    productId: null,
    value: '',
  })

  if (!product) return null

  const isSubscriptionPackage = isSubscriptionPackageProduct(product)
  const enabledSubscriptionFrequencyOptions = isSubscriptionPackage
    ? getEnabledSubscriptionFrequencyOptions(product)
    : []
  const defaultSubscriptionFrequency = isSubscriptionPackage
    ? getDefaultSubscriptionFrequency(product)
    : ''
  const selectedSubscriptionFrequency =
    isSubscriptionPackage &&
    subscriptionFrequencyState.productId === product.id &&
    enabledSubscriptionFrequencyOptions.some(
      (option) => option.id === subscriptionFrequencyState.value
    )
      ? subscriptionFrequencyState.value
      : defaultSubscriptionFrequency

  const selectedSubscriptionFrequencyLabel = getSubscriptionFrequencyLabel(
    selectedSubscriptionFrequency
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="large" viewport="contained" className="max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isSubscriptionPackage ? 'Select Tier' : 'Select Variant'}</DialogTitle>
          <DialogDescription>
            {isSubscriptionPackage
              ? `Choose a tier and frequency for ${product.name}`
              : `Choose a variant for ${product.name}`}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-96">
          <div className="space-y-3 pr-4">
            {isSubscriptionPackage ? (
              <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
                <Typography variant="labelStrong" className="block">
                  Frequency
                </Typography>
                {enabledSubscriptionFrequencyOptions.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {enabledSubscriptionFrequencyOptions.map((option) => {
                      const isSelected = option.id === selectedSubscriptionFrequency

                      return (
                        <ToggleButton
                          key={option.id}
                          type="button"
                          pressed={isSelected}
                          size="sm"
                          onClick={() =>
                            setSubscriptionFrequencyState({
                              productId: product.id,
                              value: option.id,
                            })
                          }
                          className="h-auto px-3 py-2 text-center"
                        >
                          {option.name}
                        </ToggleButton>
                      )
                    })}
                  </div>
                ) : (
                  <Typography variant="label" tone="destructive" className="block">
                    Add an available frequency before selling this subscription package.
                  </Typography>
                )}
              </div>
            ) : null}

            {product.variants?.map((variant) => {
              const variantImage = variant.media || product.media?.[0]
              const variantPrice = getPosVariantUnitPrice(variant)
              const isOutOfStock = isPosVariantOutOfStock(variant, quantityByVariantId)
              const isDisabled =
                isOutOfStock || (isSubscriptionPackage && !selectedSubscriptionFrequency)

              return (
                <ActionSurface
                  key={variant.id}
                  padding="default"
                  onClick={() =>
                    onSelectVariant(
                      variant,
                      isSubscriptionPackage
                        ? { subscriptionFrequency: selectedSubscriptionFrequency }
                        : {}
                    )
                  }
                  disabled={isDisabled}
                  className="w-full items-center gap-4"
                >
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0">
                    {variantImage ? (
                      <img
                        src={variantImage.url}
                        alt={variant.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingCart className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <Typography variant="labelStrong" className="line-clamp-1">
                      {variant.name}
                    </Typography>
                    {variant.description && (
                      <Typography
                        variant="label"
                        tone="muted"
                        className="mt-1 whitespace-pre-line line-clamp-2"
                      >
                        {variant.description}
                      </Typography>
                    )}
                    {isOutOfStock && (
                      <Typography variant="label" tone="destructive" className="mt-2 block">
                        Out of stock
                      </Typography>
                    )}
                  </div>

                  <div className="shrink-0">
                    <Typography variant="labelStrong" align="right" className="block">
                      {formatCurrency(variantPrice)}
                    </Typography>
                    {isSubscriptionPackage && selectedSubscriptionFrequencyLabel ? (
                      <Typography variant="label" tone="muted" align="right" className="block">
                        / {selectedSubscriptionFrequencyLabel.toLowerCase()}
                      </Typography>
                    ) : null}
                  </div>
                </ActionSurface>
              )
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
